import { fetchProductRecommendations } from '@lyle/algolia-recommendations'
import {
  AlgoliaProductFetcher,
  formatAlgoliaProductHit,
} from '@lyle/algolia-product-fetcher'
import { AlgoliaProductCardRenderer } from '@lyle/algolia-product-card'

const RECENTLY_VIEWED_KEY = 'previouslyViewedProducts'
const TAB_PRIORITY = ['paired', 'recommended', 'recently']

class LyleGetTheLook extends HTMLElement {
  #splides = new Map()
  #resizeObservers = new Map()
  #activeTab = null
  #cardRenderer = null
  #fetcher = null
  #config = null
  #recommendations = null
  #userSelectedTab = false
  #fetchController = null
  #tabClickHandler = null
  #recentlyViewedHandler = null

  connectedCallback() {
    this.#config = this.#parseJson('[data-product-cards-config]')
    this.#recommendations = this.#parseJson(
      '[data-get-the-look-recommendations]'
    )

    if (!this.#config) return

    this.#fetchController = new AbortController()
    this.#cardRenderer = new AlgoliaProductCardRenderer({
      config: this.#config,
      icons: window.icons || {},
      isMobile: () => window.matchMedia('(width < 1024px)').matches,
    })
    this.#fetcher = new AlgoliaProductFetcher({
      config: this.#config,
      signal: this.#fetchController.signal,
    })

    if (document.readyState === 'complete') {
      this.#setup()
    } else {
      window.addEventListener('load', () => this.#setup(), {
        once: true,
        signal: this.#fetchController.signal,
      })
    }
  }

  disconnectedCallback() {
    this.#fetchController?.abort()
    this.#fetchController = null
    if (this.#tabClickHandler) {
      this.removeEventListener('click', this.#tabClickHandler)
      this.#tabClickHandler = null
    }
    for (const splide of this.#splides.values()) {
      splide.destroy()
    }
    this.#splides.clear()
    for (const ro of this.#resizeObservers.values()) ro.disconnect()
    this.#resizeObservers.clear()
    this.#activeTab = null
    this.#userSelectedTab = false
    if (this.#recentlyViewedHandler) {
      document.removeEventListener(
        'recently-viewed:updated',
        this.#recentlyViewedHandler
      )
      this.#recentlyViewedHandler = null
    }
  }

  #setup() {
    this.#tabClickHandler = e => {
      const btn = e.target.closest('[data-get-the-look-tab]')
      if (btn) {
        this.#userSelectedTab = true
        this.#activateTab(btn.dataset.getTheLookTab)
      }
    }
    this.addEventListener('click', this.#tabClickHandler)

    const pairedPanel = this.querySelector('[data-get-the-look-panel="paired"]')
    const recommendedPanel = this.querySelector(
      '[data-get-the-look-panel="recommended"]'
    )
    const recommendedBtn = this.querySelector(
      '[data-get-the-look-tab="recommended"]'
    )

    if (pairedPanel) {
      void this.#initCarousel(pairedPanel)
      this.#activateTab('paired')
    } else if (recommendedBtn && !recommendedBtn.hidden && recommendedPanel) {
      void this.#initCarousel(recommendedPanel)
      this.#activateTab('recommended')
    }

    void this.#loadRecommendations()
    void this.#loadRecentlyViewed()
    this.#syncEmptyState()

    this.#recentlyViewedHandler = () => {
      document.removeEventListener(
        'recently-viewed:updated',
        this.#recentlyViewedHandler
      )
      this.#recentlyViewedHandler = null
      const btn = this.querySelector('[data-get-the-look-tab="recently"]')
      if (btn?.hidden) void this.#loadRecentlyViewed()
    }
    document.addEventListener(
      'recently-viewed:updated',
      this.#recentlyViewedHandler
    )
  }

  #parseJson(selector) {
    const script = this.querySelector(selector)
    if (!script?.textContent) return null

    try {
      return JSON.parse(script.textContent)
    } catch {
      return null
    }
  }

  #carouselConfig() {
    const speed = Number(this.dataset.carouselSpeed)
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const interval = Number.isFinite(speed) ? speed * 1000 : 0
    const autoplay = Boolean(interval && !reducedMotion)

    return {
      arrows: true,
      autoplay,
      gap: 'var(--spacing-5xs)',
      interval,
      mediaQuery: 'min',
      pagination: false,
      perMove: 1,
      perPage: 2,
      rewind: true,
      type: 'slide',
      breakpoints: {
        1024: {
          perPage: 4,
        },
      },
    }
  }

  async #initCarousel(panel) {
    if (this.#splides.has(panel)) return
    const el = panel.querySelector('[data-get-the-look-carousel]')
    if (!el) return
    const slideList = el.querySelector('.splide__list')
    if (!slideList || slideList.children.length === 0) return

    const { default: Splide } = await import('@splidejs/splide')
    if (!el.isConnected || this.#splides.has(panel)) return

    const splide = new Splide(el, this.#carouselConfig())
    splide.on('mounted', () => {
      this.#updateProgress(el, splide)
      this.#positionArrows(el)
    })
    splide.on('move', () => this.#updateProgress(el, splide))
    splide.mount()
    this.#splides.set(panel, splide)
    const ro = new ResizeObserver(() => this.#positionArrows(el))
    ro.observe(el)
    this.#resizeObservers.set(panel, ro)
  }

  #refreshCarousel(panel) {
    const existing = this.#splides.get(panel)
    if (existing) {
      existing.destroy()
      this.#splides.delete(panel)
    }
    const existingRo = this.#resizeObservers.get(panel)
    if (existingRo) {
      existingRo.disconnect()
      this.#resizeObservers.delete(panel)
    }
    void this.#initCarousel(panel)
  }

  #updateProgress(el, splide) {
    const bar = el.querySelector('.splide__progress__bar')
    if (!bar) return
    const end = splide.Components?.Controller?.getEnd() ?? 0
    const container = bar.closest('.splide__progress')
    if (end === 0) {
      if (container) container.style.display = 'none'
      return
    }
    if (container) container.style.display = ''
    bar.style.width = `${((splide.index + 1) / (end + 1)) * 100}%`
  }

  #positionArrows(el) {
    const imageFrame = el.querySelector('.product-card__image-frame')
    const arrowsEl = el.querySelector('.splide__arrows')
    if (!imageFrame || !arrowsEl) return
    const height = imageFrame.offsetHeight
    if (height) arrowsEl.style.top = `${height / 2}px`
  }

  #activateTab(name) {
    if (name === this.#activeTab) return

    for (const btn of this.querySelectorAll('[data-get-the-look-tab]')) {
      const active = btn.dataset.getTheLookTab === name
      btn.classList.toggle('is-active', active)
      btn.setAttribute('aria-selected', String(active))
    }

    for (const panel of this.querySelectorAll('[data-get-the-look-panel]')) {
      const active = panel.dataset.getTheLookPanel === name
      panel.hidden = !active
      if (active) void this.#initCarousel(panel)
    }

    this.#activeTab = name
  }

  #revealTab(name) {
    const btn = this.querySelector(`[data-get-the-look-tab="${name}"]`)
    if (!btn) return
    btn.hidden = false

    if (this.#userSelectedTab) return

    const curIdx = TAB_PRIORITY.indexOf(this.#activeTab ?? '')
    const newIdx = TAB_PRIORITY.indexOf(name)
    if (!this.#activeTab || newIdx < curIdx) {
      this.#activateTab(name)
    }
  }

  #syncEmptyState() {
    const section = this.closest('.get-the-look')
    if (!section) return

    const hasVisibleTab = [
      ...this.querySelectorAll('[data-get-the-look-tab]'),
    ].some(btn => !btn.hidden)
    section.classList.toggle('is-empty', !hasVisibleTab)
  }

  async #loadRecommendations() {
    const panel = this.querySelector('[data-get-the-look-panel="recommended"]')
    if (!panel) return

    const recommendations = this.#recommendations
    const hasFallback = panel.hasAttribute('data-has-fallback')

    if (!recommendations?.enabled) {
      if (hasFallback) {
        this.#revealTab('recommended')
        this.#refreshCarousel(panel)
      }
      this.#syncEmptyState()
      return
    }

    const productIds = (recommendations.productIds || [])
      .map(id => String(id || '').trim())
      .filter(Boolean)
    const canFetchWithoutProduct = recommendations.type === 'trending-items'

    if (!productIds.length && !canFetchWithoutProduct) {
      if (hasFallback) {
        this.#revealTab('recommended')
        this.#refreshCarousel(panel)
      }
      this.#syncEmptyState()
      return
    }

    try {
      const products = await this.#fetchAlgoliaRecommendations(
        recommendations,
        productIds
      )

      if (!products.length) {
        if (hasFallback) {
          this.#revealTab('recommended')
          this.#refreshCarousel(panel)
        }
        this.#syncEmptyState()
        return
      }

      const list = panel.querySelector('.splide__list')
      if (!list) return
      if (!(await this.#renderCards(list, products))) {
        if (hasFallback) {
          this.#revealTab('recommended')
          this.#refreshCarousel(panel)
        }
        this.#syncEmptyState()
        return
      }

      this.#refreshCarousel(panel)
      this.#revealTab('recommended')
      this.#syncEmptyState()
    } catch (err) {
      if (err?.name === 'AbortError') return
      if (hasFallback) {
        this.#revealTab('recommended')
        this.#refreshCarousel(panel)
      }
      this.#syncEmptyState()
    }
  }

  async #fetchAlgoliaRecommendations(recommendations, productIds) {
    const hits = await fetchProductRecommendations(
      this.#config.algolia,
      { ...recommendations, productIds },
      this.#fetchController.signal
    )
    return hits.map(hit => formatAlgoliaProductHit(hit, this.#config))
  }

  async #loadRecentlyViewed() {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY)
      if (!raw) {
        this.#syncEmptyState()
        return
      }
      const allProducts = JSON.parse(raw)
      if (!Array.isArray(allProducts) || !allProducts.length) {
        this.#syncEmptyState()
        return
      }

      const currentId = this.dataset.pageProductId
      const products = currentId
        ? allProducts.filter(p => String(p.id) !== String(currentId))
        : allProducts
      if (!products.length) {
        this.#syncEmptyState()
        return
      }

      const panel = this.querySelector('[data-get-the-look-panel="recently"]')
      if (!panel) return

      const list = panel.querySelector('.splide__list')
      if (!list) return
      if (!(await this.#renderCards(list, [...products].reverse()))) {
        this.#syncEmptyState()
        return
      }

      this.#revealTab('recently')
      this.#syncEmptyState()
    } catch {
      this.#syncEmptyState()
    }
  }

  async #renderCards(list, products) {
    const productsByHandle = await this.#fetcher.fetchProductsByHandles(
      products.map(product => product.handle)
    )
    const cards = products
      .map(product => {
        const hydrated = productsByHandle.get(product.handle)
        if (!hydrated) return null
        return {
          ...hydrated,
          __position: product.__position,
          __queryID: product.__queryID,
          objectID: product.objectID || hydrated.objectID,
        }
      })
      .filter(Boolean)

    if (!cards.length) return false

    list.innerHTML = cards
      .map(
        (product, index) =>
          `<li class="splide__slide">${this.#cardRenderer.render(product, index)}</li>`
      )
      .join('')
    await this.closest('lyle-product-cards')?.hydrate?.(list)
    return true
  }
}

if (!customElements.get('lyle-get-the-look')) {
  customElements.define('lyle-get-the-look', LyleGetTheLook)
}
